import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { useAcList, useAcSummary, type AcEvent } from "../api/accessControlApi";
import {
  ConsolePage,
  KpiRow,
  KpiTile,
  LoadState,
  METHOD_ICON,
  POINT_ICON,
  POINT_TONE,
  Panel,
  RESULT_TONE,
  StatusChip,
  StatusDot,
  ToolButton,
  relativeTime,
  toneOf,
  useTicker,
} from "../components/console/Console";
import { chartGrid, chartTick, chartTooltip } from "./SecurityDashboard";
import { NUVEX_ACCESS_BASE } from "../paths";

// ===========================================================================
// Control de acceso → Tablero: eventos en vivo (se refresca cada 10 s), accesos
// por hora, estado de puertas, torniquetes y plumas.
// ===========================================================================

export function EventRow({ e, now, compact = false }: { e: AcEvent; now: number; compact?: boolean }) {
  const { t, i18n } = useTranslation("accessConsole");
  return (
    <li className="flex items-center gap-3 px-3 py-2">
      {e.snapshot_url ? (
        <img src={e.snapshot_url} alt="" loading="lazy" className={`${compact ? "h-9 w-14" : "h-11 w-[72px]"} shrink-0 rounded object-cover`} />
      ) : (
        <span className="flex h-9 w-14 shrink-0 items-center justify-center rounded bg-subtle text-content-muted"><i className={METHOD_ICON[e.method]} /></span>
      )}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-medium text-content">
          <i className={`${e.subject_type === "vehicle" ? "ri-car-line" : "ri-user-line"} text-content-muted`} />
          <span className="truncate">{e.subject_name || t(`subject.${e.subject_type}`)}</span>
          {e.plate && <span className="rounded bg-subtle px-1 font-mono text-[10px] text-content-secondary">{e.plate}</span>}
        </p>
        <p className="truncate text-xs text-content-secondary">
          {e.point_name ?? "—"} · <i className={METHOD_ICON[e.method]} /> {t(`method.${e.method}`)} · {t(`direction.${e.direction ?? "entry"}`)}
          {e.reason ? ` · ${e.reason}` : ""}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <StatusChip tone={toneOf(RESULT_TONE, e.result)}>{t(`result.${e.result}`)}</StatusChip>
        <p className="mt-0.5 text-[11px] text-content-muted">{relativeTime(e.occurred_at, i18n.language, now)}</p>
      </div>
    </li>
  );
}

export default function AccessDashboard() {
  const { t, i18n } = useTranslation("accessConsole");
  const navigate = useNavigate();
  const now = useTicker(10_000);
  const summaryQ = useAcSummary({ refetchInterval: 10_000 });
  const pointsQ = useAcList("points", undefined, { refetchInterval: 30_000 });
  const s = summaryQ.data;

  const hourly = (s?.events.hourly ?? []).map((h) => ({
    name: new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", hour12: false }).format(new Date(h.hour)),
    [t("result.granted")]: h.granted,
    [t("result.denied")]: h.denied,
  }));

  return (
    <ConsolePage
      icon="ri-door-lock-line"
      title={t("access.title")}
      subtitle={t("access.subtitle")}
      actions={
        <>
          <ToolButton icon="ri-file-list-3-line" onClick={() => navigate(`${NUVEX_ACCESS_BASE}/control-de-acceso/bitacora`)}>{t("access.openLog")}</ToolButton>
          <ToolButton icon="ri-qr-code-line" variant="primary" onClick={() => navigate(`${NUVEX_ACCESS_BASE}/accesos-temporales/pases`)}>{t("passes.new")}</ToolButton>
        </>
      }
    >
      <LoadState loading={summaryQ.isLoading} error={summaryQ.error} empty={false} onRetry={() => summaryQ.refetch()}>
        {s && (
          <KpiRow>
            <KpiTile icon="ri-login-circle-line" label={t("kpi.events24h")} value={s.events.last24h.toLocaleString(i18n.language)} />
            <KpiTile icon="ri-shield-check-line" tone="success" label={t("kpi.granted24h")} value={s.events.granted24h} />
            <KpiTile icon="ri-shield-cross-line" tone="error" label={t("kpi.denied24h")} value={s.events.denied24h} hint={s.events.last24h ? `${Math.round((s.events.denied24h / s.events.last24h) * 100)} %` : undefined} />
            <KpiTile icon="ri-door-open-line" tone="info" label={t("kpi.pointsOnline")} value={`${s.points.byStatus.online ?? 0} / ${s.points.total}`} />
            <KpiTile icon="ri-qr-code-line" tone="primary" label={t("kpi.activePasses")} value={s.passes.byStatus.active ?? 0} hint={t("kpi.scheduledN", { interpolation: { escapeValue: false }, count: s.passes.byStatus.scheduled ?? 0 })} />
            <KpiTile icon="ri-car-line" tone="neutral" label={t("kpi.vehicles24h")} value={s.events.bySubject24h.vehicle ?? 0} hint={t("kpi.peopleN", { interpolation: { escapeValue: false }, count: s.events.bySubject24h.person ?? 0 })} />
          </KpiRow>
        )}
      </LoadState>

      <div className="grid gap-4 xl:grid-cols-5">
        <Panel
          className="xl:col-span-3"
          title={t("access.liveEvents")}
          icon="ri-pulse-line"
          actions={<span className="flex items-center gap-1.5 text-[11px] text-content-muted"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" />{t("access.autoRefresh")}</span>}
          bodyClassName="max-h-[460px] overflow-y-auto"
        >
          <LoadState loading={summaryQ.isLoading} error={null} empty={!s?.recentEvents.length} emptyText={t("events.empty")}>
            <ul className="divide-y divide-line">
              {(s?.recentEvents ?? []).map((e) => <EventRow key={e.id} e={e} now={now} />)}
            </ul>
          </LoadState>
        </Panel>
        <Panel className="xl:col-span-2" title={t("access.doors")} icon="ri-door-open-line" bodyClassName="max-h-[460px] overflow-y-auto p-2">
          <LoadState loading={pointsQ.isLoading} error={pointsQ.error} empty={!pointsQ.data?.length} emptyText={t("points.empty")}>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {(pointsQ.data ?? []).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`${NUVEX_ACCESS_BASE}/control-de-acceso/puertas`)}
                  className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-2 text-left hover:bg-subtle"
                >
                  <i className={`${POINT_ICON[p.type] ?? "ri-door-line"} text-lg text-content-muted`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-content">{p.name}</span>
                    <span className="block truncate text-[11px] text-content-muted">{p.site_name}</span>
                  </span>
                  <StatusDot tone={toneOf(POINT_TONE, p.status)} />
                </button>
              ))}
            </div>
          </LoadState>
        </Panel>
      </div>

      <Panel title={t("access.perHour")} icon="ri-bar-chart-2-line">
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
              <XAxis dataKey="name" tick={chartTick} axisLine={false} tickLine={false} interval={1} />
              <YAxis allowDecimals={false} tick={chartTick} axisLine={false} tickLine={false} width={28} />
              <Tooltip {...chartTooltip} />
              <Bar dataKey={t("result.granted")} stackId="a" fill="rgb(var(--c-success))" />
              <Bar dataKey={t("result.denied")} stackId="a" fill="rgb(var(--c-error))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </ConsolePage>
  );
}
